import { useState } from 'react';

import { useParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import {
  Box,
  Button,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  Badge,
  VStack,
  HStack,
  Divider,
  SimpleGrid,
  Link,
} from '@chakra-ui/react';

import { ExternalLinkIcon, RepeatIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';

import TokenXCreditCard from './components/TokenXCreditCard';

import { apiGetPolicyPayments } from 'api';

import type { APIPaymentOverview } from 'types/api.types';

const BillingOverview = () => {
  const { uuid } = useParams();
  const [visibleCreditCardIds, setVisibleCreditCardIds] = useState<Set<number>>(
    new Set(),
  );
  const [visibleAccountIds, setVisibleAccountIds] = useState<Set<number>>(
    new Set(),
  );
  const [isRefreshing, setIsRefreshing] = useState(false);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const { error, data, isLoading, refetch } = useQuery({
    queryKey: ['policy-payments', uuid],
    queryFn: async () => {
      const result = await apiGetPolicyPayments(uuid);
      return result;
    },
  }) as {
    data: APIPaymentOverview[];
    isLoading: boolean;
    error: any;
    refetch: any;
  };

  const toggleCreditCardVisibility = (paymentId: number) => {
    const newVisibleIds = new Set(visibleCreditCardIds);
    if (newVisibleIds.has(paymentId)) {
      newVisibleIds.delete(paymentId);
    } else {
      newVisibleIds.add(paymentId);
    }
    setVisibleCreditCardIds(newVisibleIds);
  };

  const toggleAccountVisibility = (paymentId: number) => {
    const newVisibleIds = new Set(visibleAccountIds);
    if (newVisibleIds.has(paymentId)) {
      newVisibleIds.delete(paymentId);
    } else {
      newVisibleIds.add(paymentId);
    }
    setVisibleAccountIds(newVisibleIds);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Close all TokenX components to force remount
    setVisibleCreditCardIds(new Set());
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return <Card textAlign="center">Loading billing overview...</Card>;
  }

  if (error) {
    return (
      <Card textAlign="center">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{JSON.stringify(error)}</AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      <Card bg={cardBg} p={8}>
        <HStack justify="space-between" align="center" mb={6}>
          <Heading size="md" color={textColor}>
            Billing Overview
          </Heading>
          <Button
            leftIcon={<RepeatIcon />}
            colorScheme="teal"
            variant="outline"
            onClick={handleRefresh}
            isLoading={isRefreshing}
            isDisabled={isRefreshing || isLoading}
            size="sm"
          >
            Refresh
          </Button>
        </HStack>

        {!data || data.length === 0 ? (
          <Text color="gray.500" fontStyle="italic">
            No payment methods found
          </Text>
        ) : (
          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
            {data.map((payment) => (
              <Box
                key={payment.id}
                p={6}
                border="1px"
                borderColor={borderColor}
                borderRadius="lg"
                bg={cardBg}
              >
                <VStack align="start" spacing={4}>
                  <HStack justify="space-between" w="full" align="start">
                    <VStack align="start" spacing={2}>
                      <HStack spacing={3}>
                        <Badge
                          colorScheme={payment.type === 1 ? 'blue' : 'green'}
                          fontSize="sm"
                          px={3}
                          py={1}
                          borderRadius="full"
                        >
                          {payment.type_display}
                        </Badge>
                        <Badge
                          colorScheme="gray"
                          fontSize="sm"
                          px={3}
                          py={1}
                          borderRadius="full"
                        >
                          {payment.section_display}
                        </Badge>
                      </HStack>
                      <Text fontSize="sm" color="gray.500">
                        Created: {formatDate(payment.created_at)}
                      </Text>
                    </VStack>
                  </HStack>

                  <Divider />

                  {payment.credit_card_data && (
                    <VStack align="start" spacing={3} w="full">
                      <Heading
                        size="sm"
                        color="gray.500"
                        textTransform="uppercase"
                      >
                        Credit Card Details
                      </Heading>
                      <VStack align="start" spacing={2} w="full">
                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Cardholder:
                          </Text>
                          <Text fontSize="sm">
                            {payment.credit_card_data.account_holder_name}
                          </Text>
                        </HStack>
                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Expiry Date:
                          </Text>
                          <Text fontSize="sm">
                            {payment.credit_card_data.expiry_date}
                          </Text>
                        </HStack>
                      </VStack>

                      <Button
                        size="sm"
                        colorScheme="teal"
                        variant="outline"
                        onClick={() => toggleCreditCardVisibility(payment.id)}
                      >
                        {visibleCreditCardIds.has(payment.id) ? 'Hide' : 'Show'}{' '}
                        Card Details
                      </Button>

                      {visibleCreditCardIds.has(payment.id) && (
                        <Box w="full" p={4} bg="gray.50" borderRadius="md">
                          <VStack align="start" spacing={3}>
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="gray.700"
                            >
                              Card Number & CVV:
                            </Text>
                            <TokenXCreditCard
                              token={payment.credit_card_data.token}
                              tokenExID={payment.credit_card_data.tokenExID}
                              authenticationKey={
                                payment.credit_card_data.authenticationKey
                              }
                              origin={payment.credit_card_data.origin}
                              timestamp={payment.credit_card_data.timestamp}
                              cvvToken={
                                payment.credit_card_data.cvv_token || null
                              }
                              cvvAuthenticationKey={
                                payment.credit_card_data.cvv_authentication_key ||
                                null
                              }
                              paymentId={payment.id}
                            />
                          </VStack>
                        </Box>
                      )}
                    </VStack>
                  )}

                  {payment.eft_data && (
                    <VStack align="start" spacing={3} w="full">
                      <Heading
                        size="sm"
                        color="gray.500"
                        textTransform="uppercase"
                      >
                        EFT Details
                      </Heading>
                      <VStack align="start" spacing={2} w="full">
                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Account Holder:
                          </Text>
                          <Text fontSize="sm">
                            {payment.eft_data.account_holder_name}
                          </Text>
                        </HStack>
                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Account Number:
                          </Text>
                          <HStack spacing={2}>
                            <Button
                              size="xs"
                              variant="ghost"
                              colorScheme="teal"
                              onClick={() =>
                                toggleAccountVisibility(payment.id)
                              }
                            >
                              {visibleAccountIds.has(payment.id)
                                ? 'Hide'
                                : 'Show'}
                            </Button>
                            <Text fontSize="sm" fontFamily="mono">
                              {visibleAccountIds.has(payment.id)
                                ? payment.eft_data.account_number
                                : `****${payment.eft_data.account_number.slice(
                                    -4,
                                  )}`}
                            </Text>
                          </HStack>
                        </HStack>
                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Routing Number:
                          </Text>
                          <Text fontSize="sm" fontFamily="mono">
                            {payment.eft_data.routing_number}
                          </Text>
                        </HStack>
                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Bank Number:
                          </Text>
                          <Text fontSize="sm" fontFamily="mono">
                            {payment.eft_data.bank_nr}
                          </Text>
                        </HStack>
                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Account Type:
                          </Text>
                          <Text fontSize="sm">
                            {payment.eft_data.account_type}
                          </Text>
                        </HStack>
                        <HStack justify="space-between" w="full">
                          <Text fontSize="sm" fontWeight="medium">
                            Financial Institution:
                          </Text>
                          <Text fontSize="sm">
                            {payment.eft_data.financial_institution}
                          </Text>
                        </HStack>
                        {payment.eft_data.void_cheque_file_url && (
                          <HStack justify="space-between" w="full">
                            <Text fontSize="sm" fontWeight="medium">
                              Void Cheque:
                            </Text>
                            <Link
                              href={payment.eft_data.void_cheque_file_url}
                              isExternal
                              color="teal.500"
                              fontSize="sm"
                              fontWeight="medium"
                            >
                              Download
                              <ExternalLinkIcon mx="2px" />
                            </Link>
                          </HStack>
                        )}
                      </VStack>
                    </VStack>
                  )}
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        )}
      </Card>
    </VStack>
  );
};

export default BillingOverview;
